// Parsimonious voice-leading graphs (Douthett & Steinbach 1998): connect two
// chords when ONE voice moves by ONE semitone. Over the triads (+ augmented)
// this yields "Cube Dance"; over seventh chords (+ dim7 hubs) "Power Towers".
// Everything here is computed from the definition — the edges are discovered,
// not hand-listed — so the classic results (P and L are edges, R is not; a dim7
// hub touches eight chords) fall out and are asserted in tests.

import type { PitchClass } from './notes';

export interface ParsimonyNode {
  /** Stable key, e.g. "maj:0", "aug:1", "dom7:7", "dim7:2". */
  key: string;
  kind: string;
  rootPc: PitchClass;
  pcs: PitchClass[];
  /** Symmetric chords keep one canonical root (3 dim7s, 4 augs). */
  label: string;
}

const norm = (pc: number): PitchClass => ((pc % 12) + 12) % 12;
const setKey = (pcs: number[]) => [...new Set(pcs.map(norm))].sort((a, b) => a - b).join(',');

interface KindSpec {
  kind: string;
  intervals: number[];
  /** How many distinct transpositions exist (12, or fewer for symmetric chords). */
  transpositions: number;
  suffix: string;
}

const TRIAD_KINDS: KindSpec[] = [
  { kind: 'maj', intervals: [0, 4, 7], transpositions: 12, suffix: '' },
  { kind: 'min', intervals: [0, 3, 7], transpositions: 12, suffix: 'm' },
  { kind: 'aug', intervals: [0, 4, 8], transpositions: 4, suffix: '+' },
];

const SEVENTH_KINDS: KindSpec[] = [
  { kind: 'dom7', intervals: [0, 4, 7, 10], transpositions: 12, suffix: '7' },
  { kind: 'min7', intervals: [0, 3, 7, 10], transpositions: 12, suffix: 'm7' },
  { kind: 'min7b5', intervals: [0, 3, 6, 10], transpositions: 12, suffix: 'ø7' },
  { kind: 'dim7', intervals: [0, 3, 6, 9], transpositions: 3, suffix: '°7' },
];

function buildNodes(kinds: KindSpec[], spell: (pc: PitchClass) => string): ParsimonyNode[] {
  const out: ParsimonyNode[] = [];
  for (const spec of kinds) {
    for (let root = 0; root < spec.transpositions; root++) {
      const pcs = spec.intervals.map((iv) => norm(root + iv));
      out.push({
        key: `${spec.kind}:${root}`,
        kind: spec.kind,
        rootPc: root,
        pcs,
        label: `${spell(root)}${spec.suffix}`,
      });
    }
  }
  return out;
}

export interface ParsimonyGraph {
  nodes: ParsimonyNode[];
  /** Adjacency by node key: every neighbour differs by one voice moving one semitone. */
  neighbors: Map<string, ParsimonyNode[]>;
}

function buildGraph(kinds: KindSpec[], spell: (pc: PitchClass) => string): ParsimonyGraph {
  const nodes = buildNodes(kinds, spell);
  const byPcs = new Map<string, ParsimonyNode>();
  nodes.forEach((n) => byPcs.set(setKey(n.pcs), n));

  const neighbors = new Map<string, ParsimonyNode[]>();
  for (const node of nodes) {
    const found = new Map<string, ParsimonyNode>();
    node.pcs.forEach((pc, i) => {
      for (const delta of [-1, 1]) {
        const moved = [...node.pcs];
        moved[i] = norm(pc + delta);
        if (new Set(moved).size !== moved.length) continue; // collapsed a voice
        const hit = byPcs.get(setKey(moved));
        if (hit && hit.key !== node.key) found.set(hit.key, hit);
      }
    });
    neighbors.set(node.key, [...found.values()]);
  }
  return { nodes, neighbors };
}

/** Cube Dance: 24 triads + 4 augmented, single-semitone edges (P, L and the aug slides — R moves a whole tone, so it is *not* here). */
export function cubeDanceGraph(spell: (pc: PitchClass) => string): ParsimonyGraph {
  return buildGraph(TRIAD_KINDS, spell);
}

/** Power Towers: dom7 / m7 / ø7 with the three dim7s as hubs. */
export function powerTowersGraph(spell: (pc: PitchClass) => string): ParsimonyGraph {
  return buildGraph(SEVENTH_KINDS, spell);
}
